import React from 'react';
import { useTranslation } from 'react-i18next';
import TitleWithLanterns from '../components/TitleWithLanterns';
import { Shield, Lock, FileText, Eye, UserCheck, Mail } from 'lucide-react';

const Privacy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden bg-luxury-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('/restaurant_photos/apropo_photo.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-luxury-gradient" />
        <TitleWithLanterns
          title={t('privacy.title')}
          lanternType={2}
        />
      </section>

      {/* Content */}
      <section className="py-8 md:py-12 lg:py-16 bg-white">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto">
            {/* Last Updated */}
            <p className="text-sm text-luxury-gray mb-8 text-center italic">
              {t('privacy.lastUpdated')}
            </p>

            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-luxury-gray-dark leading-relaxed">
                {t('privacy.intro')}
              </p>
            </div>

            {/* Responsible */}
            <div className="luxury-card p-8 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.responsible.title')}
                </h2>
              </div>
              <div className="ml-12 space-y-2 text-luxury-gray-dark">
                <p className="font-semibold">{t('privacy.responsible.name')}</p>
                <p>{t('privacy.responsible.address')}</p>
                <p>{t('privacy.responsible.contact')}</p>
              </div>
            </div>

            {/* Data Collected */}
            <div className="luxury-card p-8 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <Eye className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.dataCollected.title')}
                </h2>
              </div>
              <div className="ml-12 space-y-4 text-luxury-gray-dark">
                <p>{t('privacy.dataCollected.description')}</p>
                <p className="font-semibold">{t('privacy.dataCollected.technical')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacy.dataCollected.list1')}</li>
                  <li>{t('privacy.dataCollected.list2')}</li>
                  <li>{t('privacy.dataCollected.list3')}</li>
                  <li>{t('privacy.dataCollected.list4')}</li>
                </ul>
                <p className="italic">{t('privacy.dataCollected.purpose')}</p>
              </div>
            </div>

            {/* No Cookies */}
            <div className="luxury-card p-8 mb-8 bg-luxury-cream">
              <div className="flex items-start gap-4 mb-4">
                <Shield className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.noCookies.title')}
                </h2>
              </div>
              <div className="ml-12">
                <p className="text-luxury-gray-dark">{t('privacy.noCookies.description')}</p>
              </div>
            </div>

            {/* Hosting */}
            <div className="luxury-card p-8 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <Lock className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.hosting.title')}
                </h2>
              </div>
              <div className="ml-12">
                <p className="text-luxury-gray-dark">{t('privacy.hosting.description')}</p>
              </div>
            </div>

            {/* Third Party */}
            <div className="luxury-card p-8 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <UserCheck className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.thirdParty.title')}
                </h2>
              </div>
              <div className="ml-12">
                <p className="text-luxury-gray-dark">{t('privacy.thirdParty.description')}</p>
              </div>
            </div>

            {/* Your Rights */}
            <div className="luxury-card p-8 mb-8 bg-luxury-cream">
              <div className="flex items-start gap-4 mb-4">
                <Shield className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.yourRights.title')}
                </h2>
              </div>
              <div className="ml-12 space-y-4 text-luxury-gray-dark">
                <p className="font-semibold">{t('privacy.yourRights.description')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacy.yourRights.access')}</li>
                  <li>{t('privacy.yourRights.rectification')}</li>
                  <li>{t('privacy.yourRights.deletion')}</li>
                  <li>{t('privacy.yourRights.limitation')}</li>
                  <li>{t('privacy.yourRights.opposition')}</li>
                  <li>{t('privacy.yourRights.portability')}</li>
                </ul>
                <p className="font-semibold mt-4">{t('privacy.yourRights.contact')}</p>
                <p className="italic">{t('privacy.yourRights.complaint')}</p>
              </div>
            </div>

            {/* Security */}
            <div className="luxury-card p-8 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <Lock className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.security.title')}
                </h2>
              </div>
              <div className="ml-12">
                <p className="text-luxury-gray-dark">{t('privacy.security.description')}</p>
              </div>
            </div>

            {/* Updates */}
            <div className="luxury-card p-8 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display text-luxury-black">
                  {t('privacy.updates.title')}
                </h2>
              </div>
              <div className="ml-12">
                <p className="text-luxury-gray-dark">{t('privacy.updates.description')}</p>
              </div>
            </div>

            {/* Contact */}
            <div className="luxury-card p-8 bg-luxury-black text-luxury-champagne">
              <div className="flex items-start gap-4 mb-4">
                <Mail className="text-luxury-gold flex-shrink-0" size={28} />
                <h2 className="text-2xl font-display">
                  {t('privacy.contact.title')}
                </h2>
              </div>
              <div className="ml-12 space-y-2">
                <p>{t('privacy.contact.description')}</p>
                <p className="font-semibold text-luxury-gold">{t('privacy.contact.email')}</p>
                <p>{t('privacy.contact.address')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
